// THE §B.10 mapping table — the ONLY place customer-facing verdict words may
// exist. 1:1, no conditional wording, no severity gradations, no second
// mapping anywhere. A lint gate (scripts/check-display-strings.mjs, run by
// `npm run typecheck`) fails the build if the engine vocabulary appears as a
// user-visible string anywhere else in the portal tree. Counsel review of
// display framing (spec Q-4) edits THIS file and nothing else.

import { HELD_WORDS } from '../shared/heldWords.ts';

export type EngineVerdict = 'compliant' | 'non_compliant' | 'grey';

export const SUPPORT_REPORT_WORDS = {
  action: "This doesn't look right",
  title: "Tell us what looks wrong",
  note: "Short note",
  findingId: "Finding ID (optional)",
  noteHint: "Do not include patient names, health details, or identifiers.",
  submit: "Send report",
  sending: "Sending…",
  cancel: "Cancel",
  sent: "Report sent to the VerityRx support team.",
  failed: "The report could not be sent. Try again or use the support contact in the page footer.",
} as const;
export type VerdictBasis =
  | 'law_unsettled'
  | 'law_unreviewed'
  | 'source_unread'
  | 'role_not_licensed'
  | 'law_prohibited'
  | 'input_missing'
  | 'assertion_satisfied'
  | 'assertion_failed';

export function eligibilityLabel(
  verdict: EngineVerdict,
  hasConditions: boolean,
  basis?: string | null,
): string {
  if (verdict === 'compliant')
    return hasConditions ? 'Permitted with conditions' : 'Permitted';
  if (verdict === 'non_compliant') return 'Not permitted';
  // grey — the kinds must never collapse (spec B.7). Eligibility is
  // customer-independent, so a grey here is never a missing answer; but it IS
  // two different things, and saying "unresolved in law" about a cell nobody
  // has mined asserts something about the state that nobody checked
  // (2026-08-10 med-spa review, blocker 3).
  if (basis === 'source_unread') return SOURCE_UNREAD;
  if (basis === 'law_unreviewed') return NOT_YET_REVIEWED;
  if (basis === 'role_not_licensed') return ROLE_NOT_LICENSED;
  return UNRESOLVED_IN_LAW;
}

// P1-1 (C3WORDS): the practice side's grey words, by who can close the grey.
// One name each on every surface; the grid, the chips and the tiles read these.
export const GREY_WAITING_ON_YOU = 'Needs your answer';
export const GREY_NEEDS_CONFIRMATION = 'Needs your confirmation';
export const GREY_LEGAL_JUDGEMENT = 'Needs a legal judgement';
// TODO(Shawn): "Unresolved in law" (what the law_unsettled cell SAYS about the
// state) and "Needs a legal judgement" (WHO closes it) are two views of one
// state. Kept apart: the cell states the law's status only where we read it and
// it stays open; the tile and chip say whose move it is.
export const UNRESOLVED_IN_LAW = 'Unresolved in law';
// Ours, not the state's. Deliberately says who has not done the work.
export const NOT_YET_REVIEWED = 'Not yet reviewed by us';
export const SOURCE_UNREAD = 'Not yet determined: the official source could not be read';
export const sourceAttemptWords = (at?: string | null) => at ? `Last attempt: ${at.slice(0, 10)}` : 'No attempt date recorded';
// The state's, and DECIDED — the opposite of the two above. "Unresolved in
// law" told an owner the question was open when their state issues no such
// licence at all, so they waited on a review that could never come back
// favourably, or hired for a credential that does not exist. This is a grey
// nobody can act on, and saying which kind is the whole point (BL-270).
export const ROLE_NOT_LICENSED = 'This state does not license this role';
export const OFFICIAL_RECORD_LOOKUP = 'Official record look-up';
export const DEFAULT_LOOKUP_AUTHORITY = 'Official board';
export const NO_READABLE_LOOKUP = 'We could not provide an official record link for this item.';
export function officialRecordLookupLabel(authority: string, states: string[]): string {
  return `${OFFICIAL_RECORD_LOOKUP}: ${authority} (${states.join(', ')})`;
}
export const LOOKUP_UNAVAILABLE_REASON = (reason: string) => `Look-up unavailable: ${reason}`;

export function complianceLabel(verdict: EngineVerdict, basis?: string | null): string {
  if (verdict === 'compliant') return 'Meets requirements';
  if (verdict === 'non_compliant')
    // A flat prohibition and a fixable process gap are different sentences
    // (BL-199 a): "stop doing this" must never read like "change one thing".
    return basis === 'law_prohibited'
      ? 'Not permitted in this state'
      : 'Does not meet requirements';
  if (basis === 'source_unread') return SOURCE_UNREAD;
  if (basis === 'law_unreviewed') return NOT_YET_REVIEWED;
  if (basis === 'role_not_licensed') return ROLE_NOT_LICENSED;
  if (basis === 'law_unsettled') return UNRESOLVED_IN_LAW;
  return GREY_WAITING_ON_YOU;
}

export const NOT_ENTITLED_LABEL = 'State not included in your plan';

// BL-753: the landing reminder links each count to the practice section that
// already owns the outstanding work.
export const STILL_NEEDED_HEADING = 'Still needed';
export const STILL_NEEDED_EMPTY = 'Nothing is waiting on you right now.';
export const STILL_NEEDED_LOADING = 'Loading…';
export const STILL_NEEDED_RETRY = 'Try again';
export const STILL_NEEDED_FILES = 'Send us files';
export const STATE_RULE_LABEL = 'The state rule';
export const PROSE_ONLY_LIMIT = 'This limit includes several requirements. Open the state rule before answering.';
export const QUESTION_SAVED = 'Answer saved. The affected checks have been updated.';
export const QUESTION_NOT_SURE_SAVED = 'Not sure recorded. The affected checks remain open.';
export const questionGroupLabel = (people: string[], sites: string[]): string =>
  `${people.length === 1 ? people[0] : 'Shared across people'} · ${sites.length === 1 ? sites[0] : 'Shared across locations'}`;
export const questionPrompt = (token: string, person: string | null, service: string | null, site: string | null, fallback: string, state: string | null = null): string => {
  const who = person ?? 'your team';
  const work = service ?? 'this work';
  const where = site ? ` at ${site}` : '';
  const prompts: Record<string, string> = {
    gfe: `How are patients examined before ${service ?? 'treatment'}${where}?`,
    delegation: `Who oversees ${who}${where}?`,
    training: `Has ${who} completed training for ${work}?`,
    separate_licence: `Does ${who} hold the extra licence needed for ${work}?`,
    protocol: `Are written instructions in place for ${work}${where}?`,
    dea: `Does ${who} hold a current DEA registration in ${state ?? 'this state'}?`,
    legal_conditions: `What is true about ${work} by ${who}${where}?`,
  };
  return prompts[token] ?? fallback;
};
/** PM-12. A choice's words: the served label, else the code without its
 *  underscores (the server test keeps that fallback unreachable). */
export const choiceWords = (code: string, labels?: Record<string, string>): string => {
  const served = labels?.[code];
  if (served && served.trim()) return served;
  const words = code.replaceAll('_', ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
/** PM-13. One tap for the common answer, where every statement is on screen. */
export const ALL_TRUE = (n: number) => (n === 2 ? 'Both are true' : `All ${n} of these are true`);
export const ALL_TRUE_HINT = 'Sets each statement above to Yes. Change any that is not, then save.';
export const FILTER_TOGGLE = (active: number) => active ? `Filter (${active})` : 'Filter';
export const QUESTION_FILTERS_SUMMARY = (topic: string, person: string, location: string | null, state: string | null, answers: string) =>
  `Showing: ${[topic, person, location, state, answers].filter((part): part is string => !!part).join(' · ')}`;

/**
 * BL-470. The standing of a FAILURE whose state rule moved after the result was
 * computed.
 *
 * The verdict underneath is untouched — `non_compliant` on the wire, and the
 * card still carries the server's staleness sentence — because the read-side
 * drift gate must never upgrade a failure (BL-171, BL-300): telling a practice
 * "nobody has decided" about an act their state closes is the one direction
 * that is never allowed. But the chip said "Not permitted in this state" about
 * a determination we already know rests on a rule that has changed, and a flat
 * prohibition is the strongest sentence on the page.
 *
 * So this is a third thing, deliberately: it does not read as permission and it
 * does not read as a settled prohibition. It is not a fourth verdict and no
 * engine vocabulary gains a value — it is how one row's standing is SHOWN while
 * the re-check is outstanding, and it disappears the moment a recompute lands.
 */
export const BEING_RECHECKED = 'Being re-checked';

/**
 * How a machine makes its energy, in the customer's words (BL-302).
 *
 * The distinction is not cosmetic: several states write their rule around
 * "laser energy", and intense pulsed light is a broadband flashlamp, so a rule
 * naming lasers may not reach it at all. A med spa can say which box is in the
 * room; they cannot be asked whether their procedure is coherent.
 */
export const DEVICE_ENERGY: Record<string, string> = {
  laser: 'a laser',
  flashlamp: 'a lamp, not a laser',
  led: 'an LED panel',
  radiofrequency: 'radiofrequency',
  ultrasound: 'ultrasound',
  cryolipolysis: 'controlled cooling',
  electromagnetic: 'muscle stimulation',
};

// Two different sentences under ONE verdict. Both cells read "Unresolved in
// law" and both are grey, but they are not the same fact: in one, the state's
// sources are silent and nobody has said anything; in the other WE read them,
// found authorities pointing different ways, and marked the cell for a lawyer.
// A practice deciding whether to pay for an opinion needs to know which it is
// looking at. Counsel's own request, 2026-08-25 — he also asked that the
// review marker not BE the status, which is why `attorney_review` travels as
// its own field and neither of these sentences changes a verdict.
/**
 * Compact cell words for the delegation-table view of "What each role may
 * do" (Shawn's UI review, 2026-08-27: the per-row list "is not consumable").
 * Each maps 1:1 onto a full label the reader can reach by clicking the cell;
 * the asterisk on "Yes*" is explained by the legend line under the matrix.
 */
// TODO(Shawn, PMWALK PM-15 / MS-8): a third positive word, "Only if delegated",
// for cells whose limit says the service is lawful ONLY as a physician-delegated
// medical service and the person's own licence confers nothing (e.g. a Colorado
// aesthetician and dermal fillers). It changes what the grid's "Yes" MEANS, so it
// is Shawn's call (skill §9); until he decides, "Yes*" stays and the limit is read
// by opening the cell.
export function matrixWord(
  verdict: string,
  basis: string | null | undefined,
  hasConditions: boolean,
): string {
  if (verdict === 'compliant') return hasConditions ? 'Yes*' : 'Yes';
  if (verdict === 'non_compliant') return 'No';
  if (basis === 'source_unread') return SOURCE_UNREAD;
  if (basis === 'law_unreviewed') return NOT_YET_REVIEWED;
  if (basis === 'role_not_licensed') return ROLE_NOT_LICENSED;
  if (basis === 'law_unsettled') return UNRESOLVED_IN_LAW;
  return GREY_WAITING_ON_YOU;
}

/** The grid legend's grey rows: only the grey words that appear in the visible
 *  grid, each with its own mark class (P1-1). */
export const GREY_LEGEND_ORDER: readonly string[] = [
  GREY_WAITING_ON_YOU,
  UNRESOLVED_IN_LAW,
  NOT_YET_REVIEWED,
  SOURCE_UNREAD,
  ROLE_NOT_LICENSED,
];

/** Which mark family a grey grid word takes (P1-1); the diamond stays, its
 *  inside says which grey. */
export function greyKindOfWord(word: string): 'answer' | 'law' | 'ours' | 'role' {
  return word === UNRESOLVED_IN_LAW ? 'law' : word === NOT_YET_REVIEWED || word === SOURCE_UNREAD ? 'ours' : word === ROLE_NOT_LICENSED ? 'role' : 'answer';
}

/** The grey words that actually appear in the visible grid, in legend order. */
export function greyWordsInGrid(
  cells: { verdict: string; verdict_basis?: string | null; license_type: string }[],
  visibleRoles: ReadonlySet<string>,
): string[] {
  const seen = new Set(
    cells
      .filter((c) => c.verdict === 'grey' && visibleRoles.has(c.license_type))
      .map((c) => matrixWord(c.verdict, c.verdict_basis, false)),
  );
  return GREY_LEGEND_ORDER.filter((w) => seen.has(w));
}

export const MATRIX_LEGEND =
  '* Conditions apply. Open a result to read them, the source, and any lawyer review note.';

/**
 * Stage words for the legislative feed (BL-320). Wire stages are engine-side
 * vocabulary; these are the customer's words. "Proposed" not "introduced" —
 * a bill's existence is not news to act on, which is why providers can turn
 * that stage off; "Moving" carries the doc's rule-based meaning (reported out
 * of committee, passed a chamber, or a companion did).
 */
export const LEGIS_STAGE_WORDS: Record<string, string> = {
  introduced: 'Proposed',
  advancing: 'Moving',
  enacted: 'Became law',
  dead: 'No longer moving',
  proposed: 'Proposed rule',
  adopted: 'Adopted rule',
  effective: 'In effect',
};

export const COUNSEL_UNSETTLED =
  'Ask your lawyer to decide this point.';
export const COUNSEL_RECOMMENDED =
  'A lawyer should confirm this point — it is listed for counsel in your report.';
export const COUNSEL_FLAGGED =
  'The official sources point different ways. We have flagged this ' +
  'for your lawyer. Do not treat it as permitted while the question is open.';

/**
 * The review marker on a cell that DOES have an answer.
 *
 * This is deliberately not a warning. The answer stands — it is verified,
 * cited primary law — and this says only that the cell is in the queue for our
 * own lawyer's sign-off round. Wording it as a caution would undo the point:
 * a med spa that treats every marked answer as unusable is back to phoning a
 * law firm, which is the process this product replaces.
 *
 * `reason` is the mine's recorded reason and is often null, because 3,979 of
 * the 6,281 flagged cells were flagged before the reason column existed. A
 * null renders the bare sentence rather than a guess.
 */
export function attorneyReviewNote(reason?: string | null): string {
  const tail =
    reason === 'authority_conflict'
      ? ' Two sources here point different ways, so a lawyer settles which governs.'
      : reason === 'federal_overlay'
        ? ' A federal rule applies on top of your state law, so a lawyer checks both.'
        : reason === 'client_specific_fact'
          ? ' The answer depends on a detail about how your practice works that your records do not show.'
          : reason === 'device_categorisation'
            ? ' The answer depends on which device is used, so a lawyer confirms yours.'
            : reason === 'arrangement_judgement'
              ? ' Your supervision or pay arrangement needs a legal judgement.'
              : reason === 'sourcing_doubt'
                ? ' We want a second read of the source behind it.'
                : '';
  return `${COUNSEL_RECOMMENDED}${tail}`;
}

// Condition tokens are ENGINE vocabulary — "gfe", "delegation" are terms of
// art the intake surfaces are forbidden to use (D-1), and they were reaching
// the customer raw in two places: the conditions column and the "waiting on"
// line. Same rule as the verdict words: one mapping, here.
const CONDITION_WORDS: Record<string, string> = {
  gfe: 'a prescriber exam first',
  delegation: 'a named overseeing prescriber',
  training: 'documented training',
  // The licence's NAME comes from the mine and is appended after this phrase,
  // so this must read as an opening, not a whole sentence.
  separate_licence: 'a separate state licence or registration',
  legal_conditions: 'additional state requirements cited for this treatment',
  ky_led_fda_prescription_device: 'whether this LED device is an FDA prescription medical device',
  // "the state's stated limits" read as if the OWNER had to supply limits;
  // what is actually needed is their confirmation (2026-08-11 review, f13).
  conditions: "your confirmation that you operate within the state's stated limit",
  protocol: 'written standing instructions',
  physician_signed: 'whether the physician signed the written document',
  named_physician_issued_order: 'whether the named physician issued the order',
  patient_authorized_procedure: 'whether the supervisor authorized this patient’s procedure',
  adverse_event_response_ready: 'whether the supervisor can attend promptly in person',
  within_60_minutes_or_miles: 'whether the supervisor is within 60 minutes or 60 miles',
  documented_80_hour_competence: 'the performer’s documented 80 hours of training and experience',
  pa_postgraduate_hours: 'the PA’s postgraduate clinical practice hours',
  pa_specialty_hours: 'the PA’s hours in this specialty',
  collaborator_postgraduate_hours: 'the PA collaborator’s practice hours',
  pa_policies_on_file: 'whether written PA collaboration policies are on file',
  pa_agreement_on_file: 'whether a written PA collaboration agreement is on file',
  collaborator_same_specialty: 'whether the PA collaborator practices the same specialty',
  physician_specialty_trained: 'whether the physician is trained in this specialty',
  procedure_protocol_on_file: 'physician-created procedure protocol is on file',
  emergency_protocol_on_file: 'physician-created emergency protocol is on file',
  physician_or_cover_in_state: 'physician or named cover is in this state',
  prompt_personal_attendance: 'physician can promptly attend in person',
  fortnightly_site_quality_review: 'physician reviews care at this site every two weeks',
  annual_competence_reassessment: 'physician reassesses competence every year',
  initial_direct_observation: 'physician directly observed initial work',
  signed_patient_disclosure: 'patient signed the delegated-care disclosure',
  weekly_onsite_supervision: 'supervisor completes weekly on-site period',
  malpractice_coverage_current: 'required malpractice coverage is current',
  // Engine audit D3 (2026-09-23): asked once per prescriber per state.
  dea: "the prescriber's DEA registration in this state",
  licence: 'licence details',
  law: 'a rule change on our side',
  // BL-510. Not "a named overseeing prescriber": the practice HAS named one,
  // and what is still open is the training programme this state requires before
  // that prescriber may be away from the location. The specific prerequisite —
  // the hours, the approval, the documentation — is named on its own line,
  // composed by the server from the mined clause.
  delegation_route: 'the training programme that lets that prescriber be away from this location',
  // BL-558. The practice has named a prescriber and this state's rule turns on
  // where that person is while the treatment happens. Asking for the name again
  // would be false, and the result used to ask for nothing at all: it went out
  // grey with an empty "still needed" line while the named physician's own row
  // said their requirements were met.
  //
  // "THE overseeing prescriber", not "that prescriber": this line appears on the
  // named physician's OWN row too, under "You are named as the overseeing
  // prescriber…", where "that prescriber" was the software talking about the
  // reader in the third person. It is also read by whoever holds the workspace,
  // who is usually not a clinician at all (review F4).
  prescriber_presence: 'where the overseeing prescriber is while this treatment happens',
  prescriber_available_at_exam: 'whether the physician was contactable when the patient was examined',
  prescriber_available_post_treatment: 'whether the prescriber is immediately available after treatment',
  order_basis: 'which order or written instructions cover this treatment',
  authority_route: 'which lawful authority path applies',
  certified_clinical_hours: 'board-certified clinical practice hours',
  supervision_agreement_signed: 'whether a written supervision agreement is signed',
  agent_category: 'the substance or device category being treated',
  standardized_procedure_approved: 'the physician-approved written procedure',
  pharmacology_certified: 'board-certified pharmacology coursework',
  supervised_nd_count: 'how many naturopathic doctors this physician supervises',
  collaboration_policy_followed: 'whether the practice follows its collaboration policy',
  emergency_supervisor_provider_id: 'who covers emergencies during this procedure',
  emergency_availability: 'whether that person is on site or immediately available',
  emergency_appointment_possible: 'whether the delegating physician can arrange an emergency appointment',
};

export function conditionWord(token: string): string {
  return CONDITION_WORDS[token] ?? token.replace(/_/g, ' ');
}

export function conditionList(tokens: string[]): string {
  return tokens.map(conditionWord).join(', ');
}

// BL-538. Two dates a practice can act on, each named for what its writer
// actually records — and nothing named for what we do not hold.
//
//   `computed_at`  timestamptz DEFAULT now() on the verdict row (migration
//                  0045): when this engine run produced this result.
//   `cell_as_of`   the reference row's `as_of_date` PINNED at that run. The
//                  mine records it as the date the determination was written
//                  down (medspa-program/SPEC.md; the national spec's self-check
//                  pins it to the run's own date).
//
// What `as_of_date` does NOT establish is a review date: a review may well have
// happened, and this field neither says so nor says when. `medspa_cell.verified`
// is a boolean with no date column anywhere, so no verification date exists to
// show; `effective_date` belongs to a citation and only when the source states
// it. Neither may be manufactured from the two above — which is what
// "rule last reviewed <as_of_date>" did until now.
export const EVALUATED_ON = 'Evaluated';
export const REFERENCE_RECORD = 'reference record';
// `cell_live_as_of` — the reference row as it stands NOW. It is not what this
// result was computed against, so it is never printed as the record used; it is
// named separately and only when it differs, so an ordinary row stays one short
// line. The drift warning is unchanged and says what it always said.
export const CURRENT_REFERENCE = 'current reference';
// Two different unknowns, because they are two different facts. Absent means
// the field carried nothing and we can say so. Unreadable means something
// arrived that this surface cannot verify as a date — which is not evidence
// that nothing was recorded, so it must not borrow the other word.
export const DATE_NOT_RECORDED = 'not recorded';
export const DATE_UNREADABLE = 'unavailable';

// The wire formats this surface actually receives, and nothing wider. A bare
// `date` arrives from `.isoformat()` as `YYYY-MM-DD`; a `timestamptz` arrives as
// `YYYY-MM-DDTHH:MM:SS[.ffffff][±HH:MM|Z]` (a space separator is accepted
// because some drivers emit one). Anchored at both ends on purpose: a pattern
// that only anchored the front accepted `2026-09-10Tgarbage` and printed the
// date off the front of it.
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(?:Z|([+-])(\d{2}):(\d{2}))?$/;

/** Whether these three numbers are a day that exists AND that this wire can
 *  carry. Python's `date` starts at year 1, so `0000-01-01` is not a value the
 *  writer can emit and is not one this should print. */
function isRealDate(y: number, m: number, d: number): boolean {
  if (y < 1) return false;
  if (m < 1 || m > 12 || d < 1) return false;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return d <= lengths[m - 1];
}

/** A date as the practice should read it, or null when what arrived is not one.
 *
 *  The `YYYY-MM-DD` is taken LITERALLY — no `Date` parsing, no timezone
 *  conversion. `computed_at` carries an offset and `cell_as_of` does not;
 *  running either through `new Date()` and back can move the day, and a date we
 *  shifted is a date we invented. The offset is validated and then discarded:
 *  the day shown is the day the writer recorded.
 *
 *  Validation is deliberately narrow. `2026-02-31` and `2026-02-29` are not
 *  days, and a well-formed prefix does not make the rest of a string a
 *  timestamp. Anything this cannot confirm is reported as unreadable rather
 *  than rendered. */
export function plainDate(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const match = DATE_ONLY.exec(text) ?? DATE_TIME.exec(text);
  if (!match) return null;
  const [, y, m, d, hh, mi, ss, , offH, offM] = match;
  if (!isRealDate(Number(y), Number(m), Number(d))) return null;
  if (hh !== undefined) {
    // A Python `datetime` has no leap second: `:60` is not a reading this writer
    // can produce, so it is not one to accept. Nor is 24:00.
    if (Number(hh) > 23 || Number(mi) > 59 || Number(ss) > 59) return null;
    // The widest real UTC offset is ±14:00 exactly — +14:59 is not a zone.
    if (offH !== undefined) {
      const oh = Number(offH);
      const om = Number(offM);
      if (oh > 14 || om > 59 || (oh === 14 && om !== 0)) return null;
    }
  }
  return `${y}-${m}-${d}`;
}

/** What to print for one of these dates: the day, or the right kind of unknown. */
export function dateText(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return DATE_NOT_RECORDED;
  return plainDate(value) ?? DATE_UNREADABLE;
}

// BL-538 / WP-C. The coverage block's vocabulary — the words for what was
// assessed, what was not, and what closes each gap. Here for the same reason
// everything above is here: `no_cells`, `law_unreviewed` and `counsel_decides`
// are wire values, and a wire value rendered raw is the leak B.10 exists to
// stop. The server sends codes; this file is the only place they become
// sentences.
//
// A THIRD DATE, and it is not either of the two above. `recorded_at` is when
// our coverage RECORD for a state was written, and `last_read_at` is when we
// last successfully read that state's published sources. Neither is a
// substantive verification date and neither is a legal effective date, so
// neither may be shown in the row-level date line or worded like it.
export const COVERAGE_RECORDED = 'Coverage recorded';
export const SOURCES_LAST_READ = 'Sources last read';

/** The research library's freshness line. It names each watch by what it watches, so the library and
 *  Where you stand never read as one "Sources last read" with two different dates (COHERE4 P1-13):
 *  the research sources' date, then the law pages' date from the same watch Where you stand reads. */
export function researchReadLine(researchDate: string, lawDate?: string | null): string {
  const research = `Research sources last read ${researchDate}`;
  return lawDate ? `${research} · law pages last read ${lawDate}` : research;
}

// What each coverage outcome is, said to a practice owner. Every one of them
// must read as a statement about what has been CHECKED, never about whether
// anything is met — the same discipline the staffing-scope sentence keeps.
const COVERAGE_OUTCOME_WORDS: Record<string, string> = {
  result: 'assessed',
  // It IS assessed, and it is counted as assessed — said out loud, because this
  // is the one line that appears under the count AND is inside it. Without the
  // first two words a reader takes it for a seventh open item.
  role_not_recognised:
    'assessed, and this state does not recognise that role for that service',
  missing_information: 'waiting on information from you',
  unsettled_law: 'unsettled in the law',
  // Ours, and said as ours. "Not addressed" would be a claim about the state.
  never_covered: 'we have not read the law for this one yet',
  official_source_unread: SOURCE_UNREAD,
  no_cells: 'we do not cover this service in this state yet',
  uncategorised: 'we cannot categorise this one',
};

export function coverageOutcomeWord(outcome: string): string {
  return COVERAGE_OUTCOME_WORDS[outcome] ?? 'we cannot categorise this one';
}

// What closes the gap. `answer_question` is handled where the block renders,
// because it carries the question's own title and a link to its card; every
// other code is one sentence and belongs here.
const COVERAGE_STEP_WORDS: Record<string, string> = {
  add_licence: 'Add this person’s licence details under Who does what.',
  counsel_decides:
    'Your counsel decides this one. We can show them what the state publishes.',
  we_will_read_the_law: 'We read and record this one. Nothing is needed from you.',
  we_will_model_the_service:
    'We add this service for this state. Nothing is needed from you.',
  different_role:
    'Nothing closes this one: the state issues no such licence for this service.',
  we_are_looking: 'We are looking at this one.',
  we_cannot_name_it:
    'We cannot name what this one is waiting on. We are looking at it.',
};

/** The sentence for a next step, or a neutral one for a code this build does
 *  not know — an older client against a newer server must never render a raw
 *  token, and must never guess. */
export function coverageStepWord(code: string): string {
  return COVERAGE_STEP_WORDS[code] ?? 'We are looking at this one.';
}

// One line, every portal page and every export (spec B.11 / D-7).
export const FOOTER_DISCLAIMER =
  'This is not legal advice. For legal advice about your practice, consult your counsel.';

// ---- W6: first-visit welcome + practice-group "both" chooser ----
// Mirrors customer/display_strings.ts's LANDING_WELCOME_* (BL-693) so the
// two verticals' first visit reads as one product, not two.
export const LANDING_WELCOME_LEAD =
  'Add your people, locations, and services. We will show the state requirements for each.';
export const LANDING_WELCOME_PRIMARY = 'Set up your practice';
export const LANDING_WELCOME_SECONDARY = 'Import a file';
export const BOTH_PARTS_HEADING = 'Where would you like to begin?';
export const BOTH_PARTS_LEAD =
  'You have a med spa workspace and a telehealth workspace.';

export function verdictClass(verdict: EngineVerdict): string {
  // CSS hooks only — never text.
  return (
    {
      compliant: 'p-ok',
      non_compliant: 'p-bad',
      grey: 'p-grey',
    } as const
  )[verdict];
}
export const RESULTS_HELD = HELD_WORDS;
/** PM-54: an open question on a card that already MEETS its requirement is
 *  about the question's other situations, not this one. */
export const ALSO_ASKED_ELSEWHERE = 'Still asked for other situations';
export const ALSO_ASKED_TOPIC = (topic: string, others: number) =>
  others > 0 ? `${topic} · ${others} other ${others === 1 ? 'situation' : 'situations'}` : topic;
/** PM-55: a "Not permitted in this state" result says what to do about it. */
export const PROHIBITED_STOP = (person: string | null, service: string, place: string | null) =>
  `To meet this, stop ${person ?? 'this person'} performing ${service}${place ? ` at ${place}` : ''}.`;
export const PROHIBITED_CHANGE = 'Change it under Who does what';
export const PROHIBITED_ROLES = (state: string, roles: string[]) =>
  `Roles the rules in ${state} allow for this, some only on conditions: ${roles.join(', ')}. See What each role may do.`;
export const PROHIBITED_NO_ROLE = (state: string) =>
  `No role we have checked is allowed this in ${state} without an open question; see What each role may do.`;
/** PM-58: the report line — which report, when, and what counsel has done
 *  with it (served since BL-447; never shown), plus freshness. */
export const REPORT_LINE = (version: number | string, date: string | null) =>
  `Report v${version}${date ? ` · ${date}` : ''}`;
export const REPORT_SIGNED = (date: string, open: number) =>
  `Signed off by counsel on ${date}${open > 0 ? `, with ${open} ${open === 1 ? 'finding' : 'findings'} not reviewed` : ''}`;
export const REPORT_NOT_SIGNED = 'Not yet signed off by counsel';
export const REPORT_STALE = (date: string) =>
  `Your results were checked again on ${date}, after this report was built.`;
export const REPORT_STALE_LINK = 'See what changed';
/** PM-56: the lead promises only what the page holds (a source update says
 *  what changed and where, never what it means for the practice: doc 25 §1.7). */
export const LAW_CHANGES_LEAD =
  'Official pages we watch that changed, and the proposed bills that touch the rules behind your results.';
/** PM-57: while results are held the feed shows, without anything taken from them. */
export const LAW_CHANGES_HELD_NOTE =
  `${HELD_WORDS.title} ${HELD_WORDS.body} After release, this page also shows which of your checks each change sits under.`;
export const NO_STATES_OPEN = 'No states are included in your plan yet — ask us to switch one on.';
export const OWNER_BRIEF_LINK = 'Download the two-page summary';
export const PEOPLE_DETAILS_LABEL = 'Details';
export function peopleLicenceIdentifierLabel(state: string, number: string) {
  return `${state} · licence ${number}`;
}
export const PEOPLE_TABLE_LABEL = 'People in this practice';
export const PEOPLE_TABLE_HEADERS = ['Person and licence', 'Locations', 'Open items', 'Actions'] as const;
export const PEOPLE_DETAIL_LABELS = {
  locations: 'Locations',
  licences: 'Licences',
  coverage: 'State coverage',
  noLicence: 'No licence is recorded yet.',
  addLicence: 'Add a licence',
} as const;
export const PEOPLE_LICENCE_EVIDENCE_LABEL = 'Licence evidence records';
export function peopleLicenceEvidenceSummary(counts: { missing: number; declared: number; document: number; verified: number }): string {
  return [
    counts.verified ? `${counts.verified} Board checked` : '',
    counts.document ? `${counts.document} Document on file` : '',
    counts.declared ? `${counts.declared} Declared` : '',
    counts.missing ? `${counts.missing} Missing` : '',
  ].filter(Boolean).join(' · ');
}
export function peopleLocationsLabel(count: number): string {
  // PM-51: the count of DISTINCT locations a person is placed at; nobody is
  // "at 11 locations" because they are placed nowhere.
  return count ? `${count} ${count === 1 ? 'location' : 'locations'}` : 'Not placed at a location yet';
}
/** PM-50: correcting an entry in place. */
export const EDIT_ENTRY = 'Edit';
export const EDIT_ENTRY_FOR = (what: string) => `Edit ${what}`;
export const EDIT_ENTRY_SAVE = 'Save changes';
export const EDIT_ENTRY_CANCEL = 'Cancel';
export const EDIT_ENTRY_SAVED = (what: string) => `${what} updated. Connections and answers are unchanged.`;
export const EDIT_PERSON_NOTE = 'To change the role, remove this person and add them again with the right role: the role decides which rules apply.';
export const EDIT_PLACE_NOTE = 'To change the state, remove this location and add the new one: the state decides which rules apply.';
export function peopleLicenceGapsLabel(count: number): string {
  return count ? `${count} licence ${count === 1 ? 'gap' : 'gaps'}` : 'No licence gaps';
}
export function researchTopicCountLabel(title: string, count: number): string {
  return `${title} (${count})`;
}
export const REVIEWED_LAW_NOTICE = {
  heading: 'What it means for you',
  open: 'Read counsel-reviewed note',
  attribution: 'Counsel reviewed this note',
};
export const CALENDAR_COPY = {
  title: 'Calendar',
  viewLabel: 'Calendar of deadlines',
  lead: 'Every licence, registration and agreement date for your practice, with a link to the next action.',
  empty: 'No dates are coming up.',
  // PM-16: "no dates" is only reassuring when there are dates to have.
  emptyUndated: (n: number) =>
    `No dates yet: ${n} ${n === 1 ? 'licence has' : 'licences have'} no expiry date on file, so we cannot remind you before ${n === 1 ? 'it runs' : 'they run'} out.`,
  emptyUndatedAction: 'Add the dates',
  lawHeading: 'New laws taking effect',
  lawLine: (date: string) => `New law takes effect ${date}.`,
  lawMore: (n: number) => `${n} more new ${n === 1 ? 'law takes' : 'laws take'} effect later.`,
  lawLink: 'See the change →',
  subscribeHeading: 'Subscribe in your calendar app',
  subscribe: 'Add to your calendar',
  subscribed: 'Calendar link created',
  revoke: 'Replace or remove calendar link',
  week: 'This week',
  item: 'date',
  items: 'dates',
  action: 'Open the related section',
  feedHelp: 'This private link shows dates for this practice. Anyone with the link can view them. It expires after 180 days and stops working when practice access changes.',
  error: 'The calendar could not be loaded. Try again.',
  month: 'Month',
  list: 'List',
  previous: 'Previous month',
  next: 'Next month',
  nextDate: 'Next deadline',
  openCalendar: 'Open the calendar',
  officialSource: 'Official source',
  replaceFeed: 'Replace calendar link',
  removeFeed: 'Remove calendar link',
  activeFeed: 'Your calendar link is active. Anyone with the link can view these dates.',
} as const;

export const DIRECTOR_CONFIRMATION_COPY = {
  title: 'Medical director confirmation',
  lead: 'The named director signs separately for each location. This does not change a compliance result.',
  person: 'Medical director',
  choosePerson: 'Choose a person',
  account: 'Director’s sign-in account',
  chooseAccount: 'Choose an account',
  assign: 'Give signing access for this location',
  assigned: 'Signing access given. Ask the director to sign in and read the statement.',
  sign: 'Sign this statement for this location',
  signed: 'Signed for this location',
  missing: 'Record the director’s active licence for this state, then choose their sign-in account.',
} as const;

export const TRAINING_PREFILL_NOTE =
  'Suggested from this person’s recorded training answer for the same service. Check that it also applies here before saving.';
export const TRAINING_GAPS_NOTE = (count: number) =>
  `This answer will cover only ${count} unanswered ${count === 1 ? 'situation' : 'situations'}. Training answers already on record stay in place.`;
